import { useEffect, useState } from "react";

import { PopoverRadix, Tag } from "../../..";
import { Icons, theme } from "../../../../assets";
import { useUser } from "../../../../context";
import { THourControl, TProject } from "../../../../models";
import * as s from "./styled-popover-project";

type TPopoverProject = {
    trigger: JSX.Element;
    projectList: TProject[];
    onSelect?: (e: TProject) => void;
    side?: "bottom" | "top" | "right" | "left";
    align?: "end" | "start" | "center";
    sideOffset?: number;
    alignOffset?: number;
    disabled?: boolean;
    hourControlData?: THourControl[];
};

export default ({
    trigger,
    projectList,
    onSelect,
    side,
    align,
    sideOffset,
    alignOffset,
    disabled,
    hourControlData,
}: TPopoverProject) => {
    const [inputData, setInputData] = useState("");
    const [isOpen, setIsOpen] = useState(false);

    const { favoriteProject } = useUser();
    const organizeBySector = (hasFilter?: boolean) => {
        const sectorsMap = new Map<
            string,
            { sectorName: string; sectorId: string; projects: any[] }
        >();
        const unassignedProjects: any[] = [];

        projectList.forEach((project) => {
            if (!project.Sectors || project.Sectors.length === 0) {
                unassignedProjects.push({ ...project, Sectors: [] });
                return;
            }

            project.Sectors.forEach((sector) => {
                if (!sectorsMap.has(sector.Sector.id_e)) {
                    sectorsMap.set(sector.Sector.id_e, {
                        sectorName: sector.Sector.name,
                        sectorId: sector.Sector.id_e,
                        projects: [],
                    });
                }

                sectorsMap.get(sector.Sector.id_e)!.projects.push({
                    ...project,
                    Sectors: [
                        { id_e: sector.Sector.id_e, name: sector.Sector.name },
                    ],
                });
            });
        });

        if (unassignedProjects.length > 0) {
            sectorsMap.set("unassigned", {
                sectorName: "Sem setor",
                sectorId: "unassigned",
                projects: unassignedProjects,
            });
        }

        const sectors = Array.from(sectorsMap.values());

        if (hasFilter) {
            const filteredSectors = sectors
                .map((sector) => ({
                    ...sector,
                    projects: sector.projects.filter((project) =>
                        project.name
                            .toLowerCase()
                            .includes(inputData.toLowerCase())
                    ),
                }))
                .filter((sector) => sector.projects.length > 0);

            return filteredSectors;
        }

        return sectors;
    };
    const [projectsBySectors, setProjectsBySectors] = useState(
        organizeBySector()
    );

    const isTagEnabled = (project: TProject) => {
        const hourControl = hourControlData?.find(
            (data: THourControl) => data.Project.id_e === project.id_e
        );

        const reachedLimit = hourControl?.statistics?.pendingHours === 0;

        if (hourControl && hourControl.locked && reachedLimit) {
            return false;
        }

        return true;
    };

    useEffect(() => {
        if (!isOpen) setInputData("");
    }, [isOpen]);

    useEffect(() => {
        if (projectList.length) {
            setProjectsBySectors(organizeBySector(!!inputData.length));
        }
    }, [projectList, inputData]);

    return (
        <PopoverRadix
            side={side}
            sideOffset={sideOffset}
            align={align}
            alignOffset={alignOffset}
            onOpenChange={(e) => setIsOpen(e)}
            avoidCollisions={true}
            trigger={
                <s.ContainerTrigger isOpen={isOpen}>
                    {trigger}
                </s.ContainerTrigger>
            }
            open={isOpen}
        >
            <s.Container>
                {!disabled && (
                    <>
                        <div id="projects-header">
                            <span id="popover-title">Projetos</span>
                            <div id="container-input">
                                <Icons.SearchOutline />
                                <s.Input
                                    onChange={(e) => {
                                        setInputData(e.target.value);
                                    }}
                                    placeholder="Busque por projetos"
                                />
                            </div>
                        </div>
                        <div id="projects-body">
                            {projectsBySectors.map(
                                (sector) =>
                                    sector.projects.length > 0 && (
                                        <div
                                            className="container-tags"
                                            key={sector.sectorId}
                                        >
                                            <s.SubTitle>
                                                {sector.sectorName}
                                            </s.SubTitle>

                                            {sector.projects.map((project) => (
                                                <div
                                                    className="tag"
                                                    key={project.id_e}
                                                    onClick={() =>
                                                        isTagEnabled(project)
                                                            ? onSelect?.({
                                                                  ...project,
                                                                  selectedSector:
                                                                      sector.sectorId,
                                                              })
                                                            : ""
                                                    }
                                                >
                                                    <Tag
                                                        isFavorite={
                                                            favoriteProject?.id_e ===
                                                            project.id_e
                                                        }
                                                        color={
                                                            isTagEnabled(
                                                                project
                                                            )
                                                                ? project.color
                                                                : theme.gray200
                                                        }
                                                    >
                                                        {project.name}
                                                    </Tag>
                                                </div>
                                            ))}
                                        </div>
                                    )
                            )}
                            {projectsBySectors.length === 0 && (
                                <s.NotFound>
                                    <span id="error-title">
                                        Nenhum projeto encontrado
                                    </span>
                                    <span id="suggestion">
                                        Refaça sua busca ou entre em contato com
                                        gestor
                                    </span>
                                </s.NotFound>
                            )}
                        </div>
                    </>
                )}
            </s.Container>
        </PopoverRadix>
    );
};
